import { useCallback, useEffect, useState } from 'react';
import { createUser, listUsers, type AdminUserPublic } from '@/api/users';
import { UsersTable } from '@/components/UsersTable';

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUserPublic[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const list = await listUsers();
      setUsers(list);
    } catch (err) {
      setError((err as Error).message || 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submitCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      await createUser(newUsername.trim(), newPassword);
      setNewUsername('');
      setNewPassword('');
      setShowAdd(false);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Admin users</h1>
          <p className="text-slate-600 text-sm">
            Only admins can edit the CEB rates. There must always be at least one admin.
          </p>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="px-3 py-1.5 rounded-md bg-brand-500 text-slate-900 text-sm hover:bg-brand-600"
        >
          {showAdd ? 'Cancel' : 'Add admin'}
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {showAdd && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6 space-y-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Username</label>
            <input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Password (min 8 chars)</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </div>
          <button
            onClick={submitCreate}
            disabled={creating || newUsername.length < 3 || newPassword.length < 8}
            className="px-3 py-1.5 rounded-md bg-brand-500 text-slate-900 text-sm hover:bg-brand-600 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      )}

      <UsersTable users={users} onChanged={refresh} />
    </div>
  );
}
